<script setup>
// Modal de atrasos para el rol Selección / encargada (Victor 2026-05-25, P1 +
// ajustes). Al iniciar sesión, si el usuario tiene el permiso `seleccion_retoque`,
// le muestra los clientes en atraso de su sucursal (Retoque/Impresión/Álbum) una
// vez por día, con mensaje 911 sobre la calidad de la empresa. NO muestra el
// responsable. Se auto-gatea: si no es Selección o no hay atrasos, no aparece.
import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import DealsAPI from 'dashboard/api/deals';

const store = useStore();
const router = useRouter();

const visible = ref(false);
const items = ref([]);
let ran = false;

// Modal OBLIGATORIO (Victor 2026-06-09): la única salida es el botón "Clic aquí
// para dar seguimiento". Si intentan tocar un cliente, el botón VIBRA y aparece
// una flecha que apunta hacia él (Propuesta 2). No se puede cerrar de otra forma.
const nagging = ref(false); // muestra la flecha "Debes dar clic aquí ↓"
const shaking = ref(false); // dispara la vibración del botón
let nagTimer = null;
function enforce() {
  nagging.value = true;
  // Re-dispara la animación de vibración aunque ya estuviera puesta.
  shaking.value = false;
  requestAnimationFrame(() => {
    shaking.value = true;
  });
  clearTimeout(nagTimer);
  nagTimer = setTimeout(() => {
    nagging.value = false;
    shaking.value = false;
  }, 2600);
}

const currentAccountId = computed(() => store.getters.getCurrentAccountId);
const currentAccount = computed(() => store.getters.getCurrentAccount || null);
const currentUser = computed(() => store.getters.getCurrentUser || {});
const encargadaName = computed(
  () => currentUser.value?.name || currentUser.value?.available_name || ''
);
// Bandera CRUDA (no canSpecial): así NO se le muestra a super_admin/admin,
// solo a quien tiene explícitamente el permiso de Selección.
const isSeleccion = computed(
  () => currentAccount.value?.feature_permissions?.seleccion_retoque === true
);
// Encargados GENERALES de sucursal (Victor 2026-05-25): reciben el mismo aviso con
// TODOS los atrasos de su sucursal. SDO: Gabriela(41)+Sandra(25) · SDE: Victor
// Manuel(7)+Karina(6) · La Romana: Kemely(4) · Santiago: Rubi(19)+Brianna(20).
const BRANCH_MANAGER_IDS = [41, 25, 7, 6, 4, 19, 20];
const currentUserId = computed(() => store.getters.getCurrentUserID);
const isBranchManager = computed(() =>
  BRANCH_MANAGER_IDS.includes(Number(currentUserId.value))
);
const showsAlert = computed(() => isSeleccion.value || isBranchManager.value);

function seenKey() {
  const uid = store.getters.getCurrentUserID;
  const today = new Date().toISOString().slice(0, 10);
  return `seleccion_atrasos_seen_${uid}_${today}`;
}

function deptShort(name) {
  const n = (name || '').toLowerCase();
  if (n.includes('retoque')) return 'Retoque';
  if (/impresi[oó]n/.test(n)) return 'Impresión';
  if (/[aá]lbum/.test(n)) return 'Álbum';
  return name;
}

function initials(name) {
  if (!name) return '?';
  return name
    .split(' ')
    .slice(0, 2)
    .map(s => s[0]?.toUpperCase())
    .join('');
}

async function maybeShow() {
  if (!showsAlert.value) return;
  try {
    if (localStorage.getItem(seenKey())) return;
  } catch (e) {
    /* ignore */
  }
  try {
    const { data } = await DealsAPI.atrasados();
    items.value = data.payload || [];
    if (items.value.length) visible.value = true;
  } catch (e) {
    /* silencioso */
  }
}

function close() {
  try {
    localStorage.setItem(seenKey(), '1');
  } catch (e) {
    /* ignore */
  }
  visible.value = false;
}

// No obligatorio para el dueño y Gabriela (Victor 2026-06-11): Victor (1,3) y
// Gabriela Abreu (41) reciben el aviso solo como información — pueden cerrarlo
// haciendo clic fuera de la tarjeta. Para el resto sigue siendo obligatorio.
const DISMISSIBLE_USER_IDS = [1, 3, 41];
const canDismiss = computed(() =>
  DISMISSIBLE_USER_IDS.includes(Number(currentUserId.value))
);
function onBackdrop() {
  if (canDismiss.value) close();
}

// IDs (deal_id) de TODOS los clientes en atraso → el Kanban los filtra para
// mostrar EXCLUSIVAMENTE estos (Victor 2026-06-08).
function atrasadosIds() {
  return items.value
    .map(it => it.id)
    .filter(Boolean)
    .join(',');
}

// Departamento (embudo) dominante = el que más clientes en atraso tiene.
function dominantPipelineId() {
  const byPipe = {};
  items.value.forEach(it => {
    byPipe[it.pipeline_id] = (byPipe[it.pipeline_id] || 0) + 1;
  });
  let best = items.value[0]?.pipeline_id || null;
  let max = -1;
  Object.entries(byPipe).forEach(([pid, n]) => {
    if (n > max) {
      max = n;
      best = Number(pid);
    }
  });
  return best;
}

// Botón "Clic aquí para dar seguimiento": lleva al departamento con MÁS atrasos y muestra
// solo los clientes en atraso (todos los del modal).
function goToFollowUp() {
  const pid = dominantPipelineId();
  const ids = atrasadosIds();
  close();
  if (!pid) return;
  router.push({
    name: 'pipelines_view',
    params: { accountId: currentAccountId.value, pipelineId: pid },
    query: { atrasados: ids },
  });
}

watch(
  currentAccount,
  acc => {
    if (acc && acc.id && !ran) {
      ran = true;
      maybeShow();
    }
  },
  { immediate: true }
);
</script>

<template>
  <div v-if="visible" class="as-overlay" @click.self="onBackdrop">
    <div class="as-card">
      <div class="as-top">
        <div class="as-topline">
          <span class="as-mark">◆ Seguimiento obligatorio</span>
          <span class="as-badge">911</span>
        </div>
        <div class="as-h">
          <b>{{ items.length }}</b>
          {{ items.length === 1 ? 'cliente' : 'clientes' }} en atraso
        </div>
        <div class="as-scare">
          <span class="as-scare-b">Evita bloqueos en el sistema</span>
          por incumplimiento en tus labores.
        </div>
        <div class="as-who">
          Sucursal a cargo de <b>{{ encargadaName }}</b> ·
          {{ isBranchManager ? 'Encargada general' : 'Encargada de departamento' }}
        </div>
      </div>

      <div class="as-list">
        <button
          v-for="it in items"
          :key="it.id"
          type="button"
          class="as-row"
          @click="enforce"
        >
          <span class="as-av">{{ initials(it.contact_name) }}</span>
          <span class="as-info">
            <span class="as-name">{{ it.contact_name }}</span>
            <span class="as-dep">
              <span class="as-dep-g">{{ deptShort(it.pipeline_name) }}</span>
              · {{ it.days_in_stage }} días en la etapa
            </span>
          </span>
          <span class="as-pill">{{ it.days_in_stage }}d</span>
        </button>
      </div>

      <div class="as-foot">
        <div class="as-arrow" :class="{ 'as-arrow-show': nagging }">
          Debes dar clic aquí para continuar ↓
        </div>
        <button
          type="button"
          class="as-btn"
          :class="{ 'as-shake': shaking }"
          @click="goToFollowUp"
        >
          <span class="as-blink">→</span> Clic aquí para dar seguimiento
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.as-overlay {
  position: fixed;
  inset: 0;
  z-index: 9000;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 18px;
}
.as-card {
  width: 100%;
  max-width: 430px;
  background: linear-gradient(180deg, #141315, #0e0d0f);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 40px 90px -40px #000;
}
/* Cabecera premium monocroma (Propuesta 4) */
.as-top {
  padding: 22px 22px 16px;
  background: linear-gradient(180deg, rgba(201, 163, 90, 0.05), transparent);
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}
.as-topline { display: flex; align-items: center; justify-content: space-between; }
.as-mark {
  font-size: 10px;
  letter-spacing: 2.5px;
  text-transform: uppercase;
  color: #c9a35a;
  font-weight: 800;
}
.as-badge {
  font-size: 10px;
  font-weight: 800;
  color: #e0564d;
  border: 1px solid rgba(224, 86, 77, 0.3);
  border-radius: 6px;
  padding: 3px 8px;
}
.as-h { font-size: 20px; font-weight: 800; color: #eeece9; margin-top: 13px; }
.as-h b { color: #c9a35a; }
.as-scare { font-size: 12.5px; color: #e9dada; margin-top: 9px; line-height: 1.5; }
.as-scare-b { color: #e0564d; font-weight: 700; }
.as-who { font-size: 10.5px; color: #615f5a; margin-top: 11px; }
.as-who b { color: #9a9893; }
.as-list {
  display: flex;
  flex-direction: column;
  padding: 8px 16px 4px;
  max-height: 300px;
  overflow: auto;
}
.as-row {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  text-align: left;
  padding: 11px 6px;
  background: transparent;
  border: none;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
  cursor: pointer;
}
.as-row:last-child { border-bottom: none; }
.as-row:hover { background: rgba(224, 86, 77, 0.05); }
.as-av {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  color: #cdcbc6;
  background: linear-gradient(145deg, #242427, #323238);
}
.as-info { flex: 1; min-width: 0; display: flex; flex-direction: column; }
.as-name {
  font-size: 13px;
  font-weight: 600;
  color: #eeece9;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.as-dep {
  font-size: 11px;
  color: #615f5a;
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.as-dep-g { color: #c9a35a; font-weight: 600; }
.as-pill {
  font-size: 10.5px;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 6px;
  white-space: nowrap;
  flex-shrink: 0;
  color: #c9a35a;
  border: 1px solid rgba(201, 163, 90, 0.30);
}
/* Pie + botón obligatorio que parpadea/vibra + flecha (Propuesta 2) */
.as-foot { padding: 8px 18px 18px; border-top: 1px solid rgba(255, 255, 255, 0.06); position: relative; }
.as-arrow {
  text-align: center;
  color: #e0564d;
  font-size: 12px;
  font-weight: 700;
  height: 0;
  opacity: 0;
  overflow: hidden;
  transition: opacity 0.25s, height 0.25s, margin 0.25s;
}
.as-arrow-show {
  height: 20px;
  opacity: 1;
  margin: 8px 0 4px;
  animation: as-bob 1s infinite;
}
@keyframes as-bob {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(4px); }
}
.as-btn {
  position: relative;
  margin-top: 10px;
  width: 100%;
  background: linear-gradient(180deg, #221d12, #191712);
  border: 1px solid rgba(201, 163, 90, 0.30);
  color: #c9a35a;
  font-weight: 800;
  font-size: 13.5px;
  padding: 14px;
  border-radius: 13px;
  cursor: pointer;
  letter-spacing: 0.2px;
  animation: as-pulse 1.6s ease-in-out infinite;
}
.as-btn:hover { filter: brightness(1.12); }
@keyframes as-pulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(201, 163, 90, 0); border-color: rgba(201, 163, 90, 0.30); }
  50% { box-shadow: 0 0 22px 2px rgba(201, 163, 90, 0.40); border-color: #c9a35a; }
}
.as-blink { display: inline-block; animation: as-blink 1s steps(2, start) infinite; }
@keyframes as-blink { 50% { opacity: 0.3; } }
.as-shake { animation: as-shake 0.45s; }
@keyframes as-shake {
  0%, 100% { transform: translateX(0); }
  20% { transform: translateX(-7px); }
  40% { transform: translateX(7px); }
  60% { transform: translateX(-5px); }
  80% { transform: translateX(5px); }
}
</style>
